/**
 * Exploration Hook
 * 
 * Handles exploration mode functionality:
 * - Story generation on hex entry
 * - playedBy tracking
 * - Monster encounter chance
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { AxialCoord, TerrainType, TerrainHex, FactionId } from '../types/terrain';
import { generateStorySnippet, StorySnippet, StoryContext } from '../services/storyGeneration';
import { assignFactionToHex } from '../utils/factions';
import { useAuth } from '../contexts/AuthContext';
import { usePlayer } from '../contexts/PlayerContext';
import { getMonsterProbabilitySetting } from '../components/SettingsPanel';
import { getOrGenerateTerrainNarrative, getTerrainTypeName } from '../utils/terrainNarrativeManager';
// Global hex exploration removed - sponsorship system handles quest binding

export interface ExplorationState {
  currentStory: StorySnippet | null;
  isLoading: boolean;
  previousHex: AxialCoord | null;
  previousTerrain: TerrainType | null;
  isUnexploredHex?: boolean; // Flag for whether current hex is unexplored globally
}

/**
 * Hook to manage exploration state and story generation
 */
export function useExploration() {
  const { user } = useAuth();
  const { player, position, setPendingEncounter, recordSeenNarrative, seenNarratives, currentStory, setCurrentStory } = usePlayer();
  const [explorationState, setExplorationState] = useState<ExplorationState>({
    currentStory: null, // This will be synced with PlayerContext's currentStory
    isLoading: false,
    previousHex: null,
    previousTerrain: null,
  });
  
  // Use ref to track previous hex for story context (avoids stale closure issues)
  const previousHexRef = useRef<AxialCoord | null>(null);
  const previousTerrainRef = useRef<TerrainType | null>(null);
  
  // Track current hex being processed to prevent duplicate calls
  const currentProcessingHexRef = useRef<string | null>(null);
  
  // Track current story in a ref to avoid closure issues
  const currentStoryRef = useRef<StorySnippet | null>(null);
  
  // Track seen narratives in a ref for synchronous access (state updates are async)
  const seenNarrativesRef = useRef<Array<{ hexKey: string; contentId: string }>>(seenNarratives);
  
  // Keep ref in sync with state
  useEffect(() => {
    seenNarrativesRef.current = seenNarratives;
  }, [seenNarratives]);
  
  // Sync currentStory from PlayerContext to local state and ref
  useEffect(() => {
    if (currentStory) {
      console.log('📖 Restoring currentStory from PlayerContext:', currentStory);
      setExplorationState(prev => ({ ...prev, currentStory }));
      currentStoryRef.current = currentStory;
    }
  }, [currentStory]);
  
  // Wrapper callback that updates both state and ref synchronously
  const recordSeenNarrativeSync = useCallback((hexKey: string, contentId: string) => {
    // Update state via PlayerContext
    recordSeenNarrative(hexKey, contentId);
    
    // Also update ref immediately for synchronous access
    const existing = seenNarrativesRef.current.find(n => n.hexKey === hexKey);
    if (!existing) {
      seenNarrativesRef.current = [...seenNarrativesRef.current, { hexKey, contentId }];
      console.log(`📝 Ref updated synchronously: ${hexKey} → ${contentId}`);
    }
  }, [recordSeenNarrative]);

  /**
   * Handle hex entry - generate story and track playedBy
   */
  const handleHexEntry = useCallback(async (
    hex: AxialCoord,
    terrain: TerrainType,
    hexData?: TerrainHex,
    isInitialEntry?: boolean,  // If true, never generate a monster (game start)
    forcedMonsterName?: string  // If provided, force this monster (bypasses random check)
  ) => {
    const hexKey = `${hex.q},${hex.r}`;
    
    // FIX BUG #3: Prevent duplicate calls for the same hex
    if (currentProcessingHexRef.current === hexKey) {
      console.log(`🚫 useExploration - Already processing hex ${hexKey}, ignoring duplicate call`);
      // Return current story if available, otherwise null
      return currentStoryRef.current;
    }
    
    // Additional check: if we already have a story for this hex, don't regenerate
    if (currentStoryRef.current && previousHexRef.current?.q === hex.q && previousHexRef.current?.r === hex.r) {
      console.log(`🚫 useExploration - Story already exists for hex ${hexKey}, returning existing`);
      return currentStoryRef.current;
    }
    
    console.log(`🎬 useExploration - Starting hex entry for ${hexKey}`);
    
    // Mark as loading and track current hex
    currentProcessingHexRef.current = hexKey;
    setExplorationState(prev => ({ ...prev, isLoading: true }));

    // Get or assign faction
    const faction: FactionId = hexData?.faction ?? assignFactionToHex(hex);

    // Build story context using refs for previous hex
    const context: StoryContext = {
      currentHex: hex,
      terrain,
      faction,
      previousHex: previousHexRef.current || undefined,
      previousTerrain: previousTerrainRef.current || undefined,
      playerName: player.name,
    };

    try {
      // Determine if there should be a monster encounter
      // If forcedMonsterName is provided (e.g., from travel encounter), use it
      // Otherwise, use random chance based on settings
      let shouldHaveMonster: boolean;
      let selectedMonster: string | undefined;
      
      if (forcedMonsterName) {
        // Forced monster (e.g., from travel encounter) - bypass random check
        console.log(`🎯 useExploration - Using forced monster: ${forcedMonsterName}`);
        shouldHaveMonster = true;
        selectedMonster = forcedMonsterName;
      } else {
        // Normal exploration - random chance
        // Default rate: 1/12 (~8.33%) for exploration - about 1 encounter per day with 12 stamina
        // Can be overridden by settings panel for testing
        // Never generate a monster on the initial hex entry (game start) or in settlements
        const encounterRate = getMonsterProbabilitySetting(); // Returns 1/12 by default
        shouldHaveMonster = !isInitialEntry && terrain !== 4 && Math.random() < encounterRate;
        
        console.log(`🎲 useExploration - Random encounter check:`, {
          isInitialEntry,
          terrain,
          encounterRate,
          shouldHaveMonster,
          roll: Math.random()
        });
        
        // If monster encounter, select which monster BEFORE generating story
        // Use weighted terrain-based selection for natural distribution
        if (shouldHaveMonster) {
          const { selectExplorationMonster } = require('../data/bestiary');
          selectedMonster = selectExplorationMonster(terrain);
          console.log(`👹 useExploration - Selected monster: ${selectedMonster}`);
        }
      }

      let story: StorySnippet;

      // Note: Global hex exploration removed - sponsorship system handles quest binding
      
      // Check if AI is enabled - if not, use fallback (read fresh from localStorage)
      const isAIEnabled = localStorage.getItem('aiStoriesEnabled') !== 'false';
      if (!isAIEnabled) {
        const terrainNames: Record<TerrainType, string> = {
          0: 'Mountains',      // Mountain = 0
          1: 'Grasslands',     // Tundra = 1 (grassland)
          2: 'Hills',          // Hills = 2
          3: 'Forest',         // Forest = 3
          4: 'Settlement',     // Settlement = 4
          5: 'River',          // River = 5
          6: 'Desert',         // Desert = 6
          7: 'Ocean',          // Ocean = 7
        };
        const terrainName = terrainNames[terrain] || 'unknown terrain';
        story = {
          text: `You enter the ${terrainName.toLowerCase()}. The landscape stretches before you, filled with the quiet mysteries of this ancient land.`,
          hasMonster: shouldHaveMonster,
          monsterName: selectedMonster,
        };
      } else if (forcedMonsterName) {
        // Forced monster (e.g., travel encounter) - use live AI generation
        // Terrain narratives don't support monsters, so we need dynamic generation
        console.log(`🐉 useExploration - Forced monster detected, using live AI generation for: ${forcedMonsterName}`);
        story = await generateStorySnippet({ 
          ...context, 
          requestMonster: true,
          monsterName: forcedMonsterName
        });
      } else {
        // Use terrain-based narrative manager (reusable, player-tracked)
        if (user) {
          try {
            const terrainTypeName = getTerrainTypeName(terrain);
            const result = await getOrGenerateTerrainNarrative(
              terrainTypeName,
              terrain,
              hex,
              faction,
              user.uid,
              'brynlore',
              shouldHaveMonster,
              selectedMonster,
              previousHexRef.current || undefined,
              previousTerrainRef.current || undefined,
              recordSeenNarrativeSync,  // Pass sync callback that updates both state and ref
              seenNarrativesRef.current // Pass ref's current value for synchronous access
            );
            
            story = {
              text: result.narrative.storyText,
              hasMonster: result.narrative.hasMonster,
              monsterName: result.narrative.monsterName || undefined,
              audioUrl: result.narrative.audioUrl || undefined,
            };
            
            console.log(`🗺️ Terrain narrative result:`, {
              wasGenerated: result.wasGenerated,
              cached: result.cached,
              isRevisit: result.isRevisit,
              contentId: result.narrative.contentId,
            });
          } catch (error) {
            console.error('Error with terrain narrative manager, falling back to direct generation:', error);
            // Fallback to old system if terrain manager fails
            story = await generateStorySnippet({ 
              ...context, 
              requestMonster: shouldHaveMonster,
              monsterName: selectedMonster
            });
          }
        } else {
          // No user logged in, use old system
          story = await generateStorySnippet({ 
            ...context, 
            requestMonster: shouldHaveMonster,
            monsterName: selectedMonster
          });
        }
      }

      // Update refs and state with new story
      previousHexRef.current = hex;
      previousTerrainRef.current = terrain;
      
      // Persist pending encounter to PlayerContext for cross-device sync
      if (story.hasMonster && story.monsterName) {
        console.log(`💾 useExploration - Setting pendingEncounter:`, {
          monsterName: story.monsterName,
          hasText: !!story.text,
          hasAudio: !!story.audioUrl,
          forcedMonsterName: forcedMonsterName,
          hex: { q: hex.q, r: hex.r }
        });
        setPendingEncounter({
          monsterName: story.monsterName,
          storyText: story.text,
          audioUrl: story.audioUrl || null, // Ensure null instead of undefined for Firestore
          hex: { q: hex.q, r: hex.r }, // Include position for validation
        });
      } else {
        console.log(`⚠️ useExploration - Story has no monster:`, {
          hasMonster: story.hasMonster,
          monsterName: story.monsterName,
          forcedMonsterName: forcedMonsterName,
          shouldHaveMonster: shouldHaveMonster,
          selectedMonster: selectedMonster
        });
      }
      
      // Batch the state update to prevent flickering
      // Use React's automatic batching or wrap in a single setState
      setExplorationState({
        currentStory: story,
        isLoading: false,
        previousHex: hex,
        previousTerrain: terrain,
      });
      
      // Persist story to PlayerContext (saves to Firestore/localStorage)
      setCurrentStory(story);
      
      // Update refs
      currentStoryRef.current = story;
      currentProcessingHexRef.current = null;

      // TODO: Track playedBy in hex data structure
      // This will be implemented when we add hex persistence
      // For now, we just track it in local state

      return story;
    } catch (error) {
      console.error('Error handling hex entry:', error);
      setExplorationState(prev => ({ ...prev, isLoading: false }));
      // Clear processing ref on error too
      currentProcessingHexRef.current = null;
      return null;
    }
  }, [player.name, user, setCurrentStory, recordSeenNarrativeSync, seenNarrativesRef]);

  /**
   * Clear current story (e.g., when entering combat)
   */
  const clearStory = useCallback(() => {
    setExplorationState(prev => ({
      ...prev,
      currentStory: null,
    }));
    setCurrentStory(null); // Clear from PlayerContext too
    currentStoryRef.current = null;
  }, [setCurrentStory]);

  /**
   * Reset exploration state (e.g., when starting a new session)
   */
  const resetExploration = useCallback(() => {
    setExplorationState({
      currentStory: null,
      isLoading: false,
      previousHex: null,
      previousTerrain: null,
    });
    currentStoryRef.current = null;
    currentProcessingHexRef.current = null;
    previousHexRef.current = null;
    previousTerrainRef.current = null;
  }, []);

  return {
    explorationState,
    handleHexEntry,
    clearStory,
    resetExploration,
  };
}

